import { useRef, useState } from 'react';
import { useVirtual } from 'react-virtual';

import { InputText, TextArea } from 'ui/inputs';
import { GhostButton, GhostInfoButton, PrimaryButton, SecondaryButton } from 'ui/button';
import { Add16, Close16 } from 'ui/icons';
import {
  Container,
  HeaderContainer,
  HeaderText,
  InputContainer,
  InputsContainer,
  StyledGhostErrorButton,
} from './InputList.styled';

type NonFractionalProps = {
  inputList: { value: string; label: string };
  isEnabledGlobally?: boolean;
  values?: Array<number>;
  handleChange: (obj: { values: Array<number | ''>; name: string }) => void;
};

/**
 * A list of inputs used to toggle the state of non-fractional feature flags.
 * Features can also be set using 'batch mode',
 * using a comma-separated-value approach in an text area element.
 */
export const NonFractionalInputList = ({
  values = [],
  handleChange,
  inputList,
  isEnabledGlobally,
}: NonFractionalProps) => {
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkEditValues, setBulkEditValues] = useState(values.join(','));

  const onChange = (newValue: string, index: number) => {
    const newValues = [...values];
    newValues[index] = parseFloat(newValue);
    setBulkEditValues(newValues.join(','));
    handleChange({ name: inputList.value, values: newValues });
  };

  const onBulkEditChange = (newValues: string) => {
    setBulkEditValues(newValues);
  };

  const onBulkEditSubmit = () => {
    const newValues = bulkEditValues
      .split(',')
      .map((value) => value.trim())
      .map(Number);
    handleChange({ name: inputList.value, values: newValues });
    setBulkEditMode(false);
  };

  // values are kept in the editModal state as an empty string for inputs that have no value entered.
  // those empty values are filtered out before submission to the backend
  const addInputRow = () => handleChange({ name: inputList.value, values: [...values, ''] });

  const removeInputRow = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    setBulkEditValues(newValues.join(','));
    handleChange({ name: inputList.value, values: newValues });
  };

  const isOrgList = inputList.label.includes('org');
  const isEnabledList = inputList.label.toLowerCase().includes('enabled');

  const parentRef = useRef<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtual({
    size: values.length,
    parentRef,
  });

  return (
    <Container>
      <HeaderContainer>
        <HeaderText data-disabled={!!isEnabledGlobally && isEnabledList}>
          {inputList.label}
        </HeaderText>
        {isOrgList && (
          <GhostInfoButton
            isDisabled={!!isEnabledGlobally && !!isEnabledList}
            data-test="input-list-toggle-bulk-edit"
            onClick={() => setBulkEditMode((isToggledOn) => !isToggledOn)}
          >
            Bulk edit
          </GhostInfoButton>
        )}
      </HeaderContainer>
      {!bulkEditMode ? (
        <InputsContainer ref={parentRef}>
          <div
            style={{
              height: rowVirtualizer.totalSize,
            }}
          >
            {rowVirtualizer.virtualItems.map(({ index, start, key, measureRef }) => {
              const value = values[index];
              const style = {
                position: 'absolute' as any,
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${start}px)`,
              };
              return (
                <div ref={measureRef} key={key} style={style}>
                  <InputContainer data-test="input-list-row">
                    <InputText
                      data-test={inputList.value}
                      id={`${inputList.label}-${index}`}
                      isDisabled={!!isEnabledGlobally && !!isEnabledList}
                      onChange={(val) => onChange(val, index)}
                      value={`${value}`}
                    />
                    <StyledGhostErrorButton
                      data-test="input-list-remove-row"
                      isDisabled={!!isEnabledGlobally && !!isEnabledList}
                      onClick={() => removeInputRow(index)}
                    >
                      <Close16 />
                    </StyledGhostErrorButton>
                  </InputContainer>
                </div>
              );
            })}
          </div>
        </InputsContainer>
      ) : (
        <TextArea
          value={bulkEditValues}
          data-test="input-list-row-bulk-edit"
          isDisabled={!!isEnabledGlobally && !!isEnabledList}
          onChange={onBulkEditChange}
        />
      )}
      {!bulkEditMode ? (
        <GhostButton
          isDisabled={isEnabledGlobally && !!isEnabledList}
          data-test="input-list-add-row"
          onClick={addInputRow}
        >
          <Add16 />
          &nbsp;Add {isOrgList ? 'org' : 'project'}
        </GhostButton>
      ) : (
        <div>
          <PrimaryButton
            isDisabled={isEnabledGlobally && !!isEnabledList}
            data-test="input-list-submit-bulk-edit"
            onClick={onBulkEditSubmit}
          >
            Update {isOrgList ? 'org' : 'project'}s
          </PrimaryButton>
          <SecondaryButton
            data-test="input-list-cancel-bulk-edit"
            onClick={() => setBulkEditMode(false)}
          >
            Cancel
          </SecondaryButton>
        </div>
      )}
    </Container>
  );
};
